import React, { useState, useEffect } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

const roleColors = {
  Admin: "info",
  Teacher: "warning",
  Parent: "success",
  Student: "danger",
  Management: "info",
};

const ImpersonationBar = () => {
  const { impersonatedSchool, impersonatedUser, impersonateUser, stopImpersonation } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!impersonatedSchool?.id) {
      setUsers([]);
      return;
    }
    setLoading(true);
    axios
      .get(`/api/schools/${impersonatedSchool.id}/users`)
      .then((res) => {
        if (res.data.success) {
          setUsers(res.data.data);
        }
      })
      .catch((err) => console.error("Failed to load school users:", err))
      .finally(() => setLoading(false));
  }, [impersonatedSchool?.id]);

  if (!impersonatedSchool) return null;

  const userOptions = users
    .filter((u) => u.isActive)
    .map((u) => ({
      label: u.name,
      value: u.id,
      email: u.email,
      roleName: u.role?.name,
      role: u.role,
      id: u.id,
      name: u.name,
    }));

  const userOptionTemplate = (option) => {
    if (!option) return null;
    return (
      <div className="flex align-items-center gap-2">
        <span>{option.label}</span>
        <Tag
          value={option.roleName}
          severity={roleColors[option.roleName] || "info"}
          className="text-xs"
          style={{ fontSize: "0.65rem", padding: "0.1rem 0.35rem" }}
        />
      </div>
    );
  };

  const selectedTemplate = (option) => {
    if (!option) return <span className="text-400 text-sm">Select user to view as...</span>;
    return (
      <div className="flex align-items-center gap-2">
        <span className="text-sm">{option.label}</span>
        <Tag
          value={option.roleName}
          severity={roleColors[option.roleName] || "info"}
          style={{ fontSize: "0.6rem", padding: "0.1rem 0.3rem" }}
        />
      </div>
    );
  };

  if (impersonatedUser) {
    return (
      <div className="impersonation-user-banner">
        <div className="flex align-items-center gap-2">
          <i className="pi pi-user" style={{ fontSize: "0.8rem" }} />
          <span className="text-sm font-semibold">{impersonatedUser.name}</span>
          <Tag
            value={impersonatedUser.roleName}
            severity={roleColors[impersonatedUser.roleName] || "info"}
            style={{ fontSize: "0.6rem", padding: "0.1rem 0.3rem" }}
          />
        </div>
        <Button
          label="Stop"
          icon="pi pi-times"
          className="p-button-sm p-button-outlined p-button-danger"
          style={{ fontSize: "0.7rem", padding: "0.2rem 0.5rem" }}
          onClick={stopImpersonation}
        />
      </div>
    );
  }

  return (
    <div className="impersonation-user-picker">
      <label className="text-xs font-semibold text-500 mb-1 block">
        <i className="pi pi-eye mr-1" />View as User
      </label>
      <Dropdown
        value={null}
        options={userOptions}
        onChange={(e) => {
          const selected = userOptions.find((u) => u.value === e.value);
          if (selected) impersonateUser(selected);
        }}
        placeholder="Select user..."
        filter
        filterBy="label,email,roleName"
        loading={loading}
        className="w-full p-inputtext-sm"
        itemTemplate={userOptionTemplate}
        valueTemplate={selectedTemplate}
        emptyMessage="No active users found"
        panelStyle={{ maxWidth: "280px" }}
      />
    </div>
  );
};

export default ImpersonationBar;
